import React from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ICellRendererParams } from "@ag-grid-community/core";

import { getCreatedByLabel } from "fe-shared-requests/src/helpers/createdBy";
import { getFormattedDate } from "helpers/requestConverter";
import { getFormattedDateRange } from "fe-shared-requests/src/helpers/format";
import {
  getPortalColumnDefs,
  portalRenderers,
  ASSIGNEE_COL_ID,
  PRIORITY_COL_ID,
} from "fe-shared-requests/src/components/RequestsDataGrid";
import EmptyState from "fe-shared/src/components/EmptyState";
import { InboxIcon } from "fe-shared/src/components/Icons";
import { ExcelColumnConfig } from "fe-shared/src/types/common";
import { MaterialConfig } from "fe-shared/src/types/domain";

import { PickupRequestListItem } from "types";
import {
  AssigneeGridCell,
  PriorityGridCell,
  CreatedByGridCell,
  WarningGridCell,
} from "components/renderers";

export { ASSIGNEE_COL_ID, PRIORITY_COL_ID };

// A cell whose control the user is meant to operate must be listed here, or the row's click
// handler navigates to the details page before the control can open. The shared SelectInput
// renders divs, so a tag-based guard does not catch it.
export const INTERACTIVE_COL_IDS = [ASSIGNEE_COL_ID, PRIORITY_COL_ID];

export const getColumnDefs = getPortalColumnDefs;

const RequestsEmptyGridView = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      iconComponent={InboxIcon}
      title={t("pickupRequests.grid.empty.title")}
      subtitle={t("pickupRequests.grid.empty.subtitle")}
    />
  );
};

export const getRenderers = (canAssign: boolean, canEditPriority: boolean) => ({
  ...portalRenderers,
  priorityRenderer: (props: ICellRendererParams) =>
    PriorityGridCell({ data: props.data, canEditPriority }),
  createdByRenderer: CreatedByGridCell,
  assigneeRenderer: (props: ICellRendererParams) =>
    AssigneeGridCell({ ...props, canAssign }),
  warningRenderer: WarningGridCell,
  emptyGridView: RequestsEmptyGridView,
});

export const getColumnConfig = (
  t: TFunction,
  tenantName?: string,
  materialTypes: MaterialConfig[] = []
): ExcelColumnConfig<PickupRequestListItem>[] => [
  {
    width: 20,
    mappedKey: t("pickupRequests.grid.export.submittedOn"),
    getOriginalValue: (i) => getFormattedDate(i.submittedDate),
  },
  {
    width: 20,
    mappedKey: t("pickupRequests.grid.export.requestNumber"),
    getOriginalValue: (i) => i.requestNumber,
  },
  {
    width: 30,
    mappedKey: t("pickupRequests.grid.export.collector"),
    getOriginalValue: (i) => i.sourceCompanyName || "",
  },
  {
    width: 20,
    mappedKey: t("pickupRequests.grid.export.collectorEsrpNumber"),
    getOriginalValue: (i) => i.sourceCompanyRegNumber ?? "",
  },
  {
    width: 40,
    mappedKey: t("pickupRequests.grid.export.collectorAddress"),
    getOriginalValue: (i) => i.sourceCompanyAddress || "",
  },
  {
    width: 20,
    mappedKey: t("pickupRequests.grid.export.collectorPhone"),
    getOriginalValue: (i) => i.sourceCompanyPhone || "",
  },
  {
    width: 25,
    mappedKey: t("pickupRequests.grid.export.collectorName"),
    getOriginalValue: (i) => i.createdByFullName || "",
  },
  {
    width: 30,
    mappedKey: t("pickupRequests.grid.export.hauler"),
    getOriginalValue: (i) => i.targetCompanyName || "",
  },
  {
    width: 25,
    mappedKey: t("pickupRequests.grid.export.municipality"),
    getOriginalValue: (i) => i.municipalityName || "",
  },
  {
    width: 20,
    mappedKey: t("pickupRequests.grid.export.zone"),
    getOriginalValue: (i) => i.zoneName || "",
  },
  {
    width: 15,
    mappedKey: t("pickupRequests.grid.export.priority"),
    getOriginalValue: (i) =>
      t(`pickupRequests.priorities.${i.priorityCode}`, {
        defaultValue: i.priorityCode,
      }),
  },
  {
    width: 30,
    mappedKey: t("pickupRequests.grid.export.priorityReason"),
    getOriginalValue: (i) => i.priorityReason || "",
  },
  {
    width: 40,
    mappedKey: t("pickupRequests.grid.export.comments"),
    getOriginalValue: (i) => i.comment || "",
  },
  {
    width: 25,
    mappedKey: t("pickupRequests.grid.export.createdBy"),
    getOriginalValue: (i) =>
      getCreatedByLabel(t, tenantName, i.createdByFullName, i.isCreatedByInternal),
  },
  {
    width: 30,
    mappedKey: t("pickupRequests.grid.export.pickupDate"),
    getOriginalValue: (i) =>
      getFormattedDateRange(i.pickupDateFrom ?? "", i.pickupDateTo ?? ""),
  },
  {
    width: 20,
    mappedKey: t("pickupRequests.grid.export.status"),
    getOriginalValue: (i) =>
      t(`pickupRequests.statuses.${i.statusCode}`, {
        defaultValue: i.statusCode,
      }),
  },
  {
    width: 20,
    mappedKey: t("pickupRequests.grid.export.assignedTo"),
    getOriginalValue: (i) => i.assignedToFullName || "",
  },
  ...materialTypes.map(({ code, name }) => ({
    width: 15,
    mappedKey: name,
    getOriginalValue: (i: PickupRequestListItem) =>
      i.materials.find((material) => material.code === code)?.amount ?? 0,
  })),
  {
    width: 15,
    mappedKey: t("pickupRequests.grid.export.totalTires"),
    getOriginalValue: (i) =>
      i.materials.reduce((total, { amount }) => total + amount, 0),
  },
];
